import React, { useEffect, useState } from 'react';
import { XCircle, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import ReactCountryFlag from "react-country-flag";
import { useApp } from '../../context/AppContext'; // 🔥 IMPORTADO PARA NOTIFICACIONES

// 🔥 Diccionario para traducir las variables del JSON a Español
const FIELD_LABELS = {
  holder_name: "Titular",
  document_type: "Tipo de documento",
  document_number: "N° de documento",
  account_number: "N° Cuenta / Teléfono",
  bank: "Banco",
  account_type: "Tipo de cuenta",
  platform: "Plataforma",
  phone: "Teléfono",
  email: "Correo electrónico"
};

export default function TransactionModal({ txId, onClose, onRefresh }) {
  const { updateTransactionStatus } = useApp(); // 🔥 USAMOS LA FUNCIÓN DEL CONTEXTO
  const [selected, setSelected] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [adminRef, setAdminRef] = useState('');

  const formatCurrency = (value, locale = 'es-CO', currency = 'USD') => {
    if (value == null) return '-';
    try { return Number(value).toLocaleString(locale, { style: 'currency', currency }); } 
    catch { return Number(value).toLocaleString(); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: fresh, error: trxErr } = await supabase  
          .from('transactions')  
          .select(`*, payment_methods_origin(method, data), user_payment_methods:payment_method_destination_id(id, method, country_code, data, account_type, document_type, document_number, created_at)`)
          .eq('id', txId)
          .single();

        if (fresh) {
          setSelected(fresh);
          setAdminRef(fresh.destination_reference_number || '');
          if (fresh.user_id) {
            const { data: usr } = await supabase.from('users').select('id, first_name, last_name, phone, email, country, created_at, role, is_verified').eq('id', fresh.user_id).single();
            if (usr) setUserInfo(usr);
          }
        }
      } catch (err) {
        console.error("Error cargando modal:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [txId]);

  // 🔥 FUNCIÓN ACTUALIZADA PARA DISPARAR ONESIGNAL
  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      // 1. Usamos la función global que ya tiene integrada la Edge Function
      await updateTransactionStatus(txId, newStatus, adminRef);

      // 2. Auditoría (Opcional, la mantenemos aquí)
      await supabase.from('transactions_audit').insert({ 
        transaction_id: txId, 
        action: `Estado cambiado a "${newStatus}"`, 
        admin: 'admin_panel' 
      });

      onRefresh();
      onClose();
    } catch (err) {
      console.error(err);
      alert("Error al actualizar la transacción.");
    } finally {
      setUpdating(false);
    }
  };

  if (loading || !selected) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"><Loader2 className="w-10 h-10 animate-spin text-white" /></div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 sm:p-6 animate-in fade-in duration-200" onClick={onClose}>
      <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto border border-slate-200" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-slate-100 px-6 py-5 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-black text-sm tracking-widest">#{selected.id}</span>
              <h2 className="text-xl font-black text-slate-800 tracking-tight">Detalle de la Orden</h2>
            </div>
            <p className="text-xs font-bold text-slate-400 mt-1 uppercase tracking-widest">{new Date(selected.created_at).toLocaleString('es-CO')}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition"><XCircle className="w-6 h-6" /></button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* ORIGEN */}
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-700 mb-4 flex items-center gap-2">
                <ReactCountryFlag countryCode={selected.origin_country} svg style={{ width: '1.5em', height: '1.5em', borderRadius: '2px' }} /> 
                Origen
              </h3>
              
              <dl className="space-y-3 text-[13px]">
                <div className="flex justify-between border-b border-slate-200/50 pb-2"><dt className="text-slate-500 font-bold">Monto enviado</dt><dd className="font-black text-slate-800">{formatCurrency(selected.amount, 'es-CO', selected.origin_currency)}</dd></div>
                <div className="flex justify-between border-b border-slate-200/50 pb-2"><dt className="text-slate-500 font-bold">Tasa aplicada</dt><dd className="font-black text-slate-800">{selected.rate}</dd></div>
                <div className="flex justify-between border-b border-slate-200/50 pb-2"><dt className="text-slate-500 font-bold">Método de pago</dt><dd className="font-black text-slate-800">{selected.payment_methods_origin?.method || '—'}</dd></div>
                <div className="flex justify-between border-b border-slate-200/50 pb-2"><dt className="text-slate-500 font-bold">Referencia cliente</dt><dd className="font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{selected.payment_reference || 'Pendiente'}</dd></div>
                <div className="flex justify-between pt-2"><dt className="text-slate-800 font-black uppercase tracking-widest">Total a pagar</dt><dd className="font-black text-xl text-blue-700">{formatCurrency(selected.total_to_pay, 'es-CO', selected.origin_currency)}</dd></div>
              </dl>

              {selected.payment_methods_origin?.data && Object.keys(selected.payment_methods_origin.data).length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">Datos de tu cuenta (Donde recibes)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[13px]">
                    {Object.entries(selected.payment_methods_origin.data).map(([key, value]) => {
                      const translatedKey = FIELD_LABELS[key] || key.replace(/_/g, ' ');
                      return (
                        <div key={key}>
                          <dt className="text-slate-500 font-bold text-[10px] uppercase tracking-widest">{translatedKey}</dt>
                          <dd className="font-black text-slate-800 break-words">{value}</dd>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* DESTINO */}
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-700 mb-4 flex items-center gap-2">
                <ReactCountryFlag countryCode={selected.destination_country} svg style={{ width: '1.5em', height: '1.5em', borderRadius: '2px' }} /> 
                Destino
              </h3>
              <dl className="space-y-3 text-[13px]">
                <div className="flex justify-between border-b border-slate-200/50 pb-2"><dt className="text-slate-500 font-bold">Estado</dt><dd className="font-black text-slate-800 uppercase">{selected.status}</dd></div>
                <div className="flex justify-between pt-2"><dt className="text-slate-800 font-black uppercase tracking-widest">Monto a Entregar</dt><dd className="font-black text-xl text-green-600">{formatCurrency(selected.received_amount, 'es-CO', selected.destination_currency)}</dd></div>
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-2">Referencia de tu pago (Admin)</label>
                  <input type="text" value={adminRef} onChange={(e) => setAdminRef(e.target.value)} className="w-full px-4 py-2.5 border border-slate-300 rounded-xl font-bold focus:ring-2 focus:ring-blue-500 outline-none shadow-sm" placeholder="Ej: Zelle Ref #12345" />
                </div>
              </dl>
            </div>
          </div>

          {/* INFORMACIÓN DEL CLIENTE */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h3 className="text-sm font-black uppercase tracking-widest text-slate-700 mb-4">Información del Cliente</h3>
            {userInfo ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-[13px]">
                <div><dt className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">Nombre</dt><dd className="font-black text-slate-800">{userInfo.first_name} {userInfo.last_name}</dd></div>
                <div><dt className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">Teléfono</dt><dd className="font-black text-slate-800">{userInfo.phone || '-'}</dd></div>
                <div><dt className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">Email</dt><dd className="font-black text-slate-800 truncate" title={userInfo.email}>{userInfo.email || '-'}</dd></div>
                <div><dt className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">País</dt><dd className="font-black text-slate-800">{userInfo.country || '-'}</dd></div>
                <div><dt className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">Rol</dt><dd className="font-black text-slate-800 capitalize">{userInfo.role || 'Usuario'}</dd></div>
                <div><dt className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">Registro</dt><dd className="font-black text-slate-800">{formatDate(userInfo.created_at)}</dd></div>
                
                <div className="col-span-2">
                  <dt className="text-slate-400 font-bold uppercase text-[10px] tracking-widest mb-1">KYC (Verificación)</dt>
                  <dd>
                    {userInfo.is_verified ? <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-black uppercase"><ShieldCheck className="w-3 h-3"/> Verificado</span> : <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-[10px] font-black uppercase"><AlertCircle className="w-3 h-3"/> Pendiente</span>}
                  </dd>
                </div>
              </div>
            ) : <p className="text-sm text-slate-500">Cargando...</p>}
          </div>

          {/* CUENTA A TRANSFERIR */}
          <div className="bg-blue-50/50 rounded-2xl border border-blue-100 p-6">
            <h3 className="text-sm font-black uppercase tracking-widest text-blue-900 mb-4">Cuenta a transferir (Destinatario)</h3>
            {selected.user_payment_methods ? (
              <div className="bg-white rounded-xl p-5 border border-blue-100 shadow-sm">
                <p className="font-black text-blue-900 mb-3">{selected.user_payment_methods.method}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-[13px]">
                  {Object.entries(selected.user_payment_methods.data || {}).map(([key, value]) => {
                    const translatedKey = FIELD_LABELS[key] || key.replace(/_/g, ' ');
                    return (
                      <div key={key}>
                        <span className="text-slate-500 font-bold block text-[10px] uppercase tracking-widest">{translatedKey}</span>
                        <span className="font-black text-slate-800 break-words">{value}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : <p className="text-sm text-slate-500">Sin datos de destino.</p>}
          </div>

          {/* BOTONES */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-slate-100">
            <button onClick={onClose} className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-xl transition-all">Cerrar</button>
            <button onClick={() => updateStatus('Cancelada')} disabled={updating} className="px-6 py-3 bg-red-100 text-red-700 hover:bg-red-200 font-black uppercase tracking-widest text-[11px] rounded-xl transition-all">Cancelar Orden</button>
            <button onClick={() => updateStatus('Completado')} disabled={updating} className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-black uppercase tracking-widest text-[11px] rounded-xl transition-all shadow-[0_5px_15px_rgba(22,163,74,0.3)]">Marcar Pagado</button>
          </div>
        </div>
      </div>
    </div>
  );
}

